export const LEARNING_AUDIO_CACHE_LIMITS = {
  bytes: 32 * 1024 * 1024,
  fileBytes: 8 * 1024 * 1024,
  entries: 512,
  concurrent: 2,
  timeoutMs: 15000,
} as const

interface CacheEntry {
  readonly source: string
  readonly abort: AbortController
  readonly ready: Promise<string>
  readonly resolve: (url: string) => void
  readonly reject: (error: unknown) => void
  references: number
  blob?: Blob
  url?: string
}

interface CacheDependencies {
  readonly fetch?: typeof fetch
  readonly createUrl?: (blob: Blob) => string
  readonly revokeUrl?: (url: string) => void
  readonly byteLimit?: number
  readonly entryLimit?: number
  readonly timeoutMs?: number
}

export interface LearningAudioLease {
  readonly ready: Promise<string>
  readonly release: () => void
}

/** Session-memory cache only. Active audio URLs are never evicted/revoked. */
export class LearningAudioCache {
  private readonly entries = new Map<string, CacheEntry>()
  private readonly queue: CacheEntry[] = []
  private running = 0
  private disposed = false
  private readonly dependencies: CacheDependencies

  constructor(dependencies: CacheDependencies = {}) { this.dependencies = dependencies }

  get usage() {
    return { entries: this.entries.size, bytes: [...this.entries.values()].reduce((sum, entry) => sum + (entry.blob?.size ?? 0), 0) }
  }

  prepared(source: string) { return this.entries.get(source)?.url }

  acquire(source: string): LearningAudioLease {
    if (/^(blob:|data:)/i.test(source)) return { ready: Promise.resolve(source), release() {} }
    if (this.disposed) return { ready: Promise.reject(new Error('Audio cache closed')), release() {} }
    let entry = this.entries.get(source)
    if (entry?.abort.signal.aborted) { this.entries.delete(source); entry = undefined }
    if (!entry) {
      try { this.makeRoom(0, true) } catch (error) { return { ready: Promise.reject(error), release() {} } }
      let resolve!: (url: string) => void
      let reject!: (error: unknown) => void
      const ready = new Promise<string>((yes, no) => { resolve = yes; reject = no })
      entry = { source, abort: new AbortController(), ready, resolve, reject, references: 0 }
      this.entries.set(source, entry)
      this.queue.push(entry)
    } else {
      this.entries.delete(source)
      this.entries.set(source, entry)
    }
    const owned = entry
    owned.references++
    let released = false
    this.pump()
    return {
      ready: owned.ready,
      release: () => {
        if (released) return
        released = true
        owned.references--
        if (!owned.references && !owned.url) owned.abort.abort()
      },
    }
  }

  async read(source: string, signal: AbortSignal) {
    if (signal.aborted) throw new DOMException('Audio cancelled', 'AbortError')
    const lease = this.acquire(source)
    const release = () => lease.release()
    signal.addEventListener('abort', release, { once: true })
    try {
      if (signal.aborted) throw new DOMException('Audio cancelled', 'AbortError')
      const url = await lease.ready
      if (signal.aborted) throw new DOMException('Audio cancelled', 'AbortError')
      const blob = this.entries.get(source)?.blob
      // Local recording URLs are already local; never cache or revoke them.
      const bytes = blob ? await blob.arrayBuffer() : await (await (this.dependencies.fetch ?? fetch)(url, { signal })).arrayBuffer()
      if (signal.aborted) throw new DOMException('Audio cancelled', 'AbortError')
      return bytes
    } finally { signal.removeEventListener('abort', release); lease.release() }
  }

  private makeRoom(bytes: number, addingEntry = false) {
    const byteLimit = this.dependencies.byteLimit ?? LEARNING_AUDIO_CACHE_LIMITS.bytes
    const entryLimit = this.dependencies.entryLimit ?? LEARNING_AUDIO_CACHE_LIMITS.entries
    while (this.usage.bytes + bytes > byteLimit || this.entries.size + Number(addingEntry) > entryLimit) {
      const candidate = [...this.entries.values()].find(entry => !entry.references && entry.url)
      if (!candidate) throw new Error('Audio cache budget reached')
      this.entries.delete(candidate.source)
      ;(this.dependencies.revokeUrl ?? URL.revokeObjectURL)(candidate.url!)
    }
  }

  private pump() {
    while (!this.disposed && this.running < LEARNING_AUDIO_CACHE_LIMITS.concurrent && this.queue.length) {
      const entry = this.queue.shift()!
      if (entry.abort.signal.aborted) {
        entry.reject(new DOMException('Audio cancelled', 'AbortError'))
        if (this.entries.get(entry.source) === entry) this.entries.delete(entry.source)
        continue
      }
      this.running++
      void this.load(entry).finally(() => { this.running--; this.pump() })
    }
  }

  private async load(entry: CacheEntry) {
    const timer = setTimeout(() => entry.abort.abort(), this.dependencies.timeoutMs ?? LEARNING_AUDIO_CACHE_LIMITS.timeoutMs)
    try {
      // Reload avoids reusing an old no-CORS media response; the in-memory entry
      // provides reuse after this single readable download.
      const response = await (this.dependencies.fetch ?? fetch)(entry.source, { signal: entry.abort.signal, mode: 'cors', cache: 'reload' })
      if (!response.ok) throw new Error('Audio unavailable')
      if (Number(response.headers.get('content-length')) > LEARNING_AUDIO_CACHE_LIMITS.fileBytes) {
        await response.body?.cancel()
        throw new Error('Audio exceeds preload file budget')
      }
      const reader = response.body?.getReader()
      if (!reader) throw new Error('Audio stream unavailable')
      const chunks: Uint8Array<ArrayBuffer>[] = []
      let size = 0
      try {
        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          size += value.byteLength
          if (size > LEARNING_AUDIO_CACHE_LIMITS.fileBytes) throw new Error('Audio exceeds preload file budget')
          chunks.push(new Uint8Array(value))
        }
      } finally { await reader.cancel().catch(() => undefined); reader.releaseLock() }
      if (entry.abort.signal.aborted || this.disposed) throw new DOMException('Audio cancelled', 'AbortError')
      const blob = new Blob(chunks, { type: response.headers.get('content-type') ?? 'audio/mpeg' })
      this.makeRoom(blob.size)
      entry.blob = blob
      entry.url = (this.dependencies.createUrl ?? URL.createObjectURL)(blob)
      entry.resolve(entry.url)
    } catch (error) {
      entry.reject(error)
      if (this.entries.get(entry.source) === entry) this.entries.delete(entry.source)
    } finally { clearTimeout(timer) }
  }

  dispose() {
    this.disposed = true
    this.entries.forEach(entry => {
      entry.abort.abort()
      entry.reject(new DOMException('Audio cache closed', 'AbortError'))
      if (entry.url) (this.dependencies.revokeUrl ?? URL.revokeObjectURL)(entry.url)
    })
    this.entries.clear()
    this.queue.length = 0
  }
}

export const learningAudioCache = new LearningAudioCache()

/** Imperative cues keep play() in the caller's gesture and reuse warm data. */
export function createCachedLearningAudio(source: string) {
  const lease = learningAudioCache.acquire(source)
  const audio = new Audio(learningAudioCache.prepared(source) ?? source)
  void lease.ready.then(() => { if (audio.paused) lease.release() }, lease.release)
  audio.addEventListener('ended', lease.release, { once: true })
  audio.addEventListener('error', lease.release, { once: true })
  audio.addEventListener('pause', lease.release, { once: true })
  return audio
}
